// The podium set past the finish on the lake side, facing the grandstand: three timber blocks
// with maroon skirts and gold numbers, a Cat Derby backdrop, flower urns and festoon lights.
// Cats stand on `spots` (1st, 2nd, 3rd) for the results.
import {
  AdditiveBlending, Color, Group, IcosahedronGeometry, InstancedMesh, Matrix4, Mesh,
  MeshBasicMaterial, MeshStandardMaterial, PlaneGeometry, Quaternion, SphereGeometry, Vector3,
  type BufferGeometry,
} from 'three';
import { PODIUM, podiumSpots, rng } from './layout';
import type { EnvMaterials } from './materials';
import { podiumFaces } from './signs';
import { PALETTE, SIGN_FONT, box, canvasTexture, drawCrown, drawPaw, merge } from './util';

export class Podium {
  readonly group = new Group();
  /** Top centre of the 1st, 2nd and 3rd blocks, in world space. */
  readonly spots: Vector3[] = podiumSpots().map((s) => new Vector3(s.x, s.y, s.z));
  /** Direction the podium faces (towards the grandstand). */
  readonly facing = new Vector3(0, 0, -1);
  private flowers: InstancedMesh[] = [];

  constructor(mats: EnvMaterials) {
    this.group.name = 'podium';
    const blockW = 1.45, blockD = 1.3;
    const timber: BufferGeometry[] = [], skirts: BufferGeometry[] = [];
    const faces = podiumFaces();
    const faceMat = new MeshStandardMaterial({ map: faces, roughness: 0.6 });
    this.spots.forEach((s, i) => {
      const h = s.y;
      // Timber block with a top board and a maroon skirt below the number plate.
      timber.push(box(blockW, 0.08, blockD, { at: [s.x, h - 0.04, s.z], tile: 1.2 }));
      skirts.push(box(blockW - 0.04, h - 0.08, blockD - 0.04, { at: [s.x, (h - 0.08) / 2, s.z], tile: 1.2 }));
      const g = new PlaneGeometry(Math.min(0.9, h * 0.95), Math.min(0.9, h * 0.95));
      const uv = g.getAttribute('uv');
      for (let k = 0; k < uv.count; k++) uv.setX(k, (i + uv.getX(k)) / 3);
      g.rotateY(Math.PI); // face −z
      const plate = new Mesh(g, faceMat);
      plate.position.set(s.x, h / 2, s.z - blockD / 2 + 0.005);
      this.group.add(plate);
    });
    // A low stage under the blocks.
    timber.push(box(6.4, 0.16, 3.2, { at: [PODIUM.x, 0.08, PODIUM.z + 0.3], tile: 1.8, shade: 0.85 }));
    const timberMesh = new Mesh(merge(timber), mats.timber);
    const skirtMesh = new Mesh(merge(skirts), mats.paintedMaroon);
    for (const mm of [timberMesh, skirtMesh]) { mm.castShadow = true; mm.receiveShadow = true; this.group.add(mm); }
    const trim = new Mesh(merge(this.spots.map((s) => box(blockW + 0.02, 0.05, 0.05, { at: [s.x, s.y - 0.1, s.z - blockD / 2], tile: 1 }))), mats.gold);
    this.group.add(trim);

    // Backdrop: a timber frame with a plum banner, crown and wordmark.
    const bdW = 8.5, bdH = 3.4, bdZ = PODIUM.z + 2.6;
    const frame = new Mesh(merge([
      box(0.18, bdH + 0.9, 0.18, { at: [PODIUM.x - bdW / 2, (bdH + 0.9) / 2, bdZ], tile: 1.2, grain: 'y' }),
      box(0.18, bdH + 0.9, 0.18, { at: [PODIUM.x + bdW / 2, (bdH + 0.9) / 2, bdZ], tile: 1.2, grain: 'y' }),
      box(bdW + 0.5, 0.2, 0.22, { at: [PODIUM.x, bdH + 0.9, bdZ], tile: 1.2 }),
    ]), mats.timber);
    frame.castShadow = true;
    this.group.add(frame);
    const backdrop = canvasTexture(1280, 512, (g, w, h) => {
      g.fillStyle = PALETTE.plum; g.fillRect(0, 0, w, h);
      g.strokeStyle = PALETTE.gold; g.lineWidth = 10; g.strokeRect(18, 18, w - 36, h - 36);
      drawCrown(g, w / 2, 120, 150, PALETTE.gold);
      g.font = `900 150px ${SIGN_FONT}`; g.textAlign = 'center'; g.textBaseline = 'middle';
      g.fillStyle = PALETTE.gold; g.fillText('CAT DERBY', w / 2, 300, 1000);
      g.font = `900 46px ${SIGN_FONT}`; g.fillStyle = PALETTE.ivory; g.fillText('CHAMPIONS', w / 2, 420);
      for (const x of [140, w - 140]) drawPaw(g, x, h / 2 + 30, 130, PALETTE.wine);
    });
    const bd = new Mesh(new PlaneGeometry(bdW, bdH).rotateY(Math.PI), new MeshStandardMaterial({ map: backdrop, roughness: 0.85 }));
    bd.position.set(PODIUM.x, bdH / 2 + 0.6, bdZ - 0.1);
    bd.receiveShadow = true;
    this.group.add(bd);

    // Flower urns either side and a festoon of bulbs over the backdrop.
    const urnMat = new MeshStandardMaterial({ color: '#d9c9a8', roughness: 0.6 });
    const r = rng(61);
    const blooms = new InstancedMesh(new IcosahedronGeometry(0.1, 0), new MeshStandardMaterial({ roughness: 0.6 }), 120);
    const leaves = new InstancedMesh(new IcosahedronGeometry(0.16, 0), new MeshStandardMaterial({ color: '#365f25', roughness: 0.8 }), 80);
    const colours = ['#ff5fa8', '#ffffff', '#ffd23f', '#b46cff'].map((c) => new Color(c));
    const m = new Matrix4(), q = new Quaternion(), s = new Vector3(), p = new Vector3();
    let b = 0, l = 0;
    for (const ux of [PODIUM.x - 3.6, PODIUM.x + 3.6]) {
      const urn = new Mesh(new SphereGeometry(0.35, 14, 10, 0, Math.PI * 2, 0, Math.PI * 0.62), urnMat);
      urn.rotation.x = Math.PI; urn.position.set(ux, 0.75, PODIUM.z + 0.6);
      const stem = new Mesh(box(0.22, 0.55, 0.22, { at: [ux, 0.3, PODIUM.z + 0.6], tile: 1 }), urnMat);
      for (const mm of [urn, stem]) { mm.castShadow = true; this.group.add(mm); }
      for (let k = 0; k < 40; k++) leaves.setMatrixAt(l++ % 80, m.compose(p.set(ux + (r() - 0.5) * 0.7, 0.85 + r() * 0.4, PODIUM.z + 0.6 + (r() - 0.5) * 0.7), q.random(), s.setScalar(0.8 + r() * 0.6)));
      for (let k = 0; k < 60; k++) {
        blooms.setMatrixAt(b, m.compose(p.set(ux + (r() - 0.5) * 0.8, 0.9 + r() * 0.5, PODIUM.z + 0.6 + (r() - 0.5) * 0.8), q.random(), s.setScalar(0.7 + r() * 0.6)));
        blooms.setColorAt(b++, colours[Math.floor(r() * colours.length)]!);
      }
    }
    for (const im of [blooms, leaves]) { im.castShadow = true; this.group.add(im); }
    this.flowers = [blooms, leaves];
    const bulbMat = new MeshBasicMaterial({ color: new Color('#ffd49a').multiplyScalar(4.5) });
    const haloMat = new MeshBasicMaterial({ color: '#ffb066', transparent: true, opacity: 0.2, blending: AdditiveBlending, depthWrite: false });
    const nB = 21;
    const bulbs = new InstancedMesh(new SphereGeometry(0.06, 8, 6), bulbMat, nB);
    const halos = new InstancedMesh(new IcosahedronGeometry(0.2, 1), haloMat, nB);
    for (let k = 0; k < nB; k++) {
      const t = k / (nB - 1);
      m.compose(p.set(PODIUM.x - bdW / 2 + t * bdW, bdH + 0.75 - Math.sin(t * Math.PI) * 0.5, bdZ - 0.25), q.identity(), s.setScalar(1));
      bulbs.setMatrixAt(k, m); halos.setMatrixAt(k, m);
    }
    this.group.add(bulbs, halos);
  }

  /** Shows or hides the flowers in the urns (they are out from late spring to early autumn). */
  setFlowers(on: boolean): void {
    for (const f of this.flowers) f.visible = on;
  }
}
